import DeviceType from '../DeviceType';
import { STORE_KEY } from './index';

// Auslesen und Loeschen aller Speicherorte, in denen die App etwas ablegt.
//
// Warum eigenstaendig und nicht ueber StorageHandler: der liefert bewusst den
// ERSTEN brauchbaren Wert aus seiner Kette (keymanager -> localStorage ->
// Cookie -> sessionStorage) und spiegelt dabei sogar zurueck (_restore). Fuer
// die Diagnose ist genau das falsch - hier soll sichtbar werden, was in JEDEM
// Speicher WIRKLICH steht, samt Abweichungen zwischen ihnen. Deshalb greift
// dieser Helfer die vier Speicher einzeln und direkt ab.
//
// Der interne Speicher ist Samsung-only: tizen.keymanager (Orsay laeuft ueber
// denselben Pfad). LG hat kein Pendant - die mitgelieferte hcap.js kennt
// keinerlei Storage-Namespace, dort SIND Cookie/localStorage/sessionStorage
// bereits alles.

/** Lange Werte werden gekuerzt - der Fingerprint allein hat 64 Zeichen. */
const MAX_VALUE_CHARS = 120;
/** Obergrenze je Speicher, damit die get_info-Antwort nicht ausufert. */
const MAX_ENTRIES = 50;

/**
 * Die Schluessel, die die App kennt. Fuer Cookie/localStorage/sessionStorage
 * nur zur Sortierung - die lassen sich vollstaendig durchzaehlen. Fuer den
 * keymanager sind sie die EINZIGE Quelle: er kann getData(name), aber es gibt
 * kein Verzeichnis, das man auflisten koennte.
 */
export const KNOWN_KEYS = Object.keys(STORE_KEY).map((k) => STORE_KEY[k]);

function shorten(value) {
  const s = value === null || value === undefined ? '' : String(value);
  if (s.length <= MAX_VALUE_CHARS) return s;
  return s.slice(0, MAX_VALUE_CHARS) + '… (' + s.length + ' Zeichen)';
}

/** Bekannte Schluessel nach vorn, der Rest alphabetisch dahinter. */
function sortEntries(entries) {
  return entries.sort((a, b) => {
    const ka = KNOWN_KEYS.indexOf(a.key);
    const kb = KNOWN_KEYS.indexOf(b.key);
    if (ka !== -1 && kb !== -1) return ka - kb;
    if (ka !== -1) return -1;
    if (kb !== -1) return 1;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });
}

function readWebStorage(store) {
  const entries = [];
  try {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key === null) continue;
      let value;
      try {
        value = store.getItem(key);
      } catch (e) {
        value = '<nicht lesbar>';
      }
      entries.push({ key: key, value: shorten(value) });
    }
  } catch (e) {
    return { error: String((e && e.message) || e), entries: [] };
  }
  return { entries: sortEntries(entries) };
}

function readCookies() {
  const entries = [];
  try {
    const raw = String(document.cookie || '');
    if (!raw) return { entries: [] };
    raw.split(';').forEach((part) => {
      const p = part.replace(/^\s+/, '');
      if (!p) return;
      const eq = p.indexOf('=');
      const key = eq === -1 ? p : p.slice(0, eq);
      const value = eq === -1 ? '' : p.slice(eq + 1);
      if (!key) return;
      entries.push({ key: key, value: shorten(value) });
    });
  } catch (e) {
    return { error: String((e && e.message) || e), entries: [] };
  }
  return { entries: sortEntries(entries) };
}

/** tizen.keymanager, sofern vorhanden. Sonst null. */
function keymanager() {
  try {
    const km = window.tizen && window.tizen.keymanager;
    if (!km || typeof km.getData !== 'function') return null;
    return km;
  } catch (e) {
    return null;
  }
}

function readInternal() {
  if (DeviceType.isLG) {
    return { available: false, reason: 'LG/WebOS hat keinen internen Schlüsselspeicher', entries: [] };
  }
  const km = keymanager();
  if (!km) {
    return { available: false, reason: 'tizen.keymanager nicht verfügbar', entries: [] };
  }
  // Nur die bekannten Schluessel - der keymanager laesst sich nicht auflisten.
  const entries = [];
  KNOWN_KEYS.forEach((name) => {
    let value;
    try {
      value = km.getData({ name: name });
    } catch (e) {
      value = undefined;
    }
    if (value === undefined || value === null || value === '') return;
    entries.push({ key: name, value: shorten(value) });
  });
  return { available: true, entries: entries };
}

/**
 * Eintraege deckeln und die tatsaechliche Anzahl festhalten. Liefert ein neues
 * Objekt, damit die Lesefunktionen ihre Rueckgabe nicht nachtraeglich
 * umgeschrieben bekommen.
 */
function cap(store) {
  const total = store.entries.length;
  if (total <= MAX_ENTRIES) return Object.assign({}, store, { total: total });
  return Object.assign({}, store, {
    total: total,
    entries: store.entries.slice(0, MAX_ENTRIES),
    truncated: total - MAX_ENTRIES,
  });
}

export default class StorageInspector {
  /**
   * Was in den vier Speichern steht.
   *
   * Rueckgabe je Speicher: { entries: [{key, value}], truncated, error? } und
   * fuer den internen zusaetzlich { available, reason? }.
   */
  static read() {
    return {
      cookie: cap(readCookies()),
      local: cap(readWebStorage(window.localStorage)),
      session: cap(readWebStorage(window.sessionStorage)),
      internal: cap(readInternal()),
    };
  }

  /**
   * Alles loeschen: Cookies, localStorage und sessionStorage vollstaendig, dazu
   * die bekannten Schluessel im internen Speicher.
   *
   * SCHWERWIEGEND: damit sind device_id, bid, room und fingerprint weg - das
   * Geraet muss danach neu registriert werden. Der Aufrufer ist dafuer
   * zustaendig, vorher zu fragen; dieser Helfer fuehrt nur aus.
   *
   * Rueckgabe: ein Bericht, was tatsaechlich entfernt wurde.
   */
  static clearAll() {
    const before = StorageInspector.read();
    const report = { cookie: 0, local: 0, session: 0, internal: [], errors: [] };

    // Cookies richtig ablaufen lassen, nicht nur leeren: CookieStorage.remove
    // schreibt einen leeren Wert, der Name bleibt dann stehen.
    try {
      const names = readCookies().entries.map((e) => e.key);
      names.forEach((name) => {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
      });
      report.cookie = names.length;
    } catch (e) {
      report.errors.push('cookie: ' + String((e && e.message) || e));
    }

    try {
      report.local = window.localStorage.length;
      window.localStorage.clear();
    } catch (e) {
      report.errors.push('localStorage: ' + String((e && e.message) || e));
    }

    try {
      report.session = window.sessionStorage.length;
      window.sessionStorage.clear();
    } catch (e) {
      report.errors.push('sessionStorage: ' + String((e && e.message) || e));
    }

    const km = keymanager();
    if (km && typeof km.removeData === 'function') {
      KNOWN_KEYS.forEach((name) => {
        try {
          km.removeData({ name: name });
          report.internal.push(name);
        } catch (e) {
          // Ein Schluessel, den es gar nicht gab, wirft hier ebenfalls - das ist
          // kein Fehler, der den Lauf abbrechen sollte.
        }
      });
    }

    report.beforeCounts = {
      cookie: before.cookie.total,
      local: before.local.total,
      session: before.session.total,
      internal: before.internal.total,
    };
    return report;
  }
}



// WEBPACK FOOTER //
// ./src/helpers/StorageHandler/Inspector.js